/**
 * Purpose: tell image models the exact storey stack and plan shapes to render,
 *          so they do not invent a generic Swedish box.
 *
 * Input:  {@link BuildingDesign} (local meters). Optional map screenshot is
 *         camera/site context only — not an excuse to reshape the massing.
 * Output: free-text massing summary + geometry JSON (plans, heights, programs).
 * Consumers: Generate image dialog system prompt, design-ai render controller.
 */

import { BuildingDesign } from '@shared/models/design/building-design.model';
import { Point2D } from '@shared/models/geometry.model';
import { Shape, Storey } from '@shared/models/design/storey.model';
import { designGuards } from './design-typeguards.helpers';
import {
  getCutoutShapes,
  getSolidShapes,
  getStoreyGfaExcludingCutouts,
} from './cutout.helpers';
import { getDesignGfa } from './shape.helpers';
import {
  getAllStoreys,
  getStoreyElevation,
  getStoreys,
} from './storey.helpers';
import { isEqualPoints } from '../geometry/geometry.helpers';
import { roundToDecimals } from '../math.helpers';
import { guards } from '../typeguards.helpers';
import { activityRefGuards } from './guards/activity-guards.helpers';
import {
  ActivityType,
  type ProgramActivityType,
} from '@shared/models/design/activities/activity-type.model';

const SETBACK_RATIO = 0.85;
const OVERHANG_RATIO = 1.15;

const PROGRAM_FACADE_LABEL: Record<ProgramActivityType, string> = {
  [ActivityType.Residential]: 'residential apartments',
  [ActivityType.Retail]: 'retail / shops',
  [ActivityType.Office]: 'office',
  [ActivityType.School]: 'school',
  [ActivityType.Industry]: 'industrial / logistics',
  [ActivityType.Garage]: 'garage / parking',
};

const PROGRAM_FACADE_HINT: Record<ProgramActivityType, string> = {
  [ActivityType.Residential]: 'apartment windows',
  [ActivityType.Retail]: 'shopfronts and larger glazing',
  [ActivityType.Office]: 'office fenestration',
  [ActivityType.School]: 'school fenestration',
  [ActivityType.Industry]: 'hall / industrial facade',
  [ActivityType.Garage]: 'parking-garage openings',
};

/**
 * Hard massing rules shared by the dialog system prompt and server fallbacks.
 * Image models follow numbered constraints better than a single prose sentence.
 */
export const BUILDING_RENDER_GEOMETRY_RULES = [
  'Massing rules (override style if they conflict):',
  '1. Exact storey count per building. Use the storey lock / massing summary. Count the stacked floor bands of each building in the map screenshot when one is attached. Do not add or omit floors, and do not copy the tallest count onto a shorter building.',
  '2. The building bodies are only the massing already shown (every footprint in the map screenshot and/or JSON prisms). Do not add building bodies, wings, towers, penthouses, annexes, utbyggnader, or extra floors.',
  "3. Each storey's outer silhouette must match the map massing and that storey's plan polygons. Keep a rectangular plan as a box. Keep an L/U/T/courtyard plan. Do not round or sculpt it into a more interesting form.",
  '4. Different footprints between storeys are real setbacks or overhangs — not extra masses.',
  '5. Cutouts stay empty voids. Do not fill courtyards or atria.',
  '6. Roof is always a flat slab on the top-storey outline unless the user description explicitly asks for a sloping or pitched roof. No dormers, extra roof volumes, or a different plan.',
  "7. If a map screenshot is attached, TRACE that 3D massing. Same camera, same silhouettes, each building's own floor-box count. Photorealism is a material skin on that form — not a new design.",
  '8. JSON confirms storey count per building and that no extra masses exist. Do not rebuild a different, more complex building from the coordinates than what the screenshot shows.',
  '9. height_m is floor-to-floor. Floor bands on the traced body must match each storey height. A 4.5 m storey must look taller than a 3.0 m storey.',
  '10. Program on a storey sets facade character for that floor only. Do not add volumes or balconies unless the user description asks.',
  '11. Photorealism is materials and light. It is not permission to add floors or utbyggnader.',
  '12. Sit the building on flat, level ground. No slope, berm, podium, or hill. Do not add a storey, plinth, garage, crawl space, or extra floor area under the building unless it is specified (screenshot box or elevation_m < 0).',
].join('\n');

/**
 * Trace the map screenshot so image models copy the visible massing
 * instead of inventing storeys and wings from coordinates.
 */
export const BUILDING_RENDER_SCREENSHOT_TRACE = [
  'Trace the attached map screenshot (this is the site massing):',
  '1. The 3D massing already in the map view IS the site. Keep every building, that exact silhouette, footprint, setbacks, and stacked floor bands.',
  "2. Count the stacked floor boxes per building in the screenshot. That is that building's storey count. Do not add a floor. Do not drop a floor. Do not invent a hidden extra storey on the back. Do not copy the tallest count onto a shorter building.",
  '3. Do not add wings, annexes, or utbyggnader that are not in the screenshot. The only volumes are the ones already drawn.',
  '4. Keep the same camera: same pitch, bearing, and how the building sits on the site.',
  '5. Replace map-style shading with photorealistic façades. Do not redraw the form to look more architectural.',
  '6. Balconies are allowed only if the user description asks; they attach to this traced body and must not add a new building body.',
  '7. Roof is a flat slab on the top box unless the user description explicitly asks for a slope.',
  '8. Sit the building on flat, level ground. Do not add a plinth, basement, or extra floor under the lowest box in the screenshot.',
].join('\n');

/**
 * JSON as a checklist when a screenshot is already the form to trace.
 */
export const BUILDING_RENDER_JSON_CHECKLIST = [
  'Geometry JSON checklist (do not invent a different building from these coordinates):',
  '- Confirm the screenshot floor-band count matches the JSON storey stack per building.',
  '- Confirm there are no masses in the photo that are not in the JSON, and none missing — including nothing extra under the building.',
  '- Use height_m only to proportion the traced floor bands.',
  '- Roof is flat unless the user description asks for a slope.',
  '- If JSON and screenshot disagree, keep the screenshot silhouette and the JSON storey count per building — never add extra volumes to fix it.',
].join('\n');

/**
 * Step-by-step extrusion from the attached geometry JSON.
 * Image models skip coordinate rings unless told to construct before styling.
 */
export const BUILDING_RENDER_JSON_CONSTRUCTION = [
  'Construct THIS massing from the geometry JSON before applying style:',
  '1. Read buildings[]. For each building, read storeys[] bottom → top. Each storey is one floor band of height_m meters (floor-to-floor).',
  '2. For every solid shape, plot every [x, y] vertex in order and close the ring. The 3D body must keep that vertex count and every jog / re-entrant corner.',
  '3. Extrude that exact polygon as a vertical prism from elevation_m to elevation_m + height_m. Rectangular rings stay boxy. No taper, no sculpted corners, no extra chamfers that change the plan, no merging separate shapes into one block, no added building bodies.',
  '4. If the ring is L, U, T, courtyard, or otherwise concave, keep that shape. Do not complete it into a rectangle or fill the envelope. If the ring is rectangular, keep it as a box — do not break it into extra volumes.',
  '5. Stack floors on their real elevations. A smaller upper polygon is a setback; a larger one is an overhang. Do not average storeys into one extrusion.',
  '6. cutout: true is an empty hole through that storey. Keep it empty.',
  '7. The lowest storey sits on flat, level ground. Do not add a storey, plinth, or extra floor area below the lowest elevation_m unless that storey is specified with elevation_m < 0.',
  '8. Roof is always a flat slab on the top-storey outline unless the user description explicitly asks for a sloping roof.',
  '9. Then apply look from the user description. Balconies are allowed only if the description asks; they attach to this prism and must not add a new building body or change the plan.',
].join('\n');

/**
 * Field-by-field instructions for the attached geometry JSON.
 * Image models will not guess extrusion, axes, or cutouts from raw keys.
 */
export const GEOMETRY_JSON_LEGEND = [
  'How to read the geometry JSON (this is the 3D building, not a style hint):',
  '- Axes: +x = east, +y = south, +z = up. Units are meters. +y is south, not up.',
  '- buildings[] is one footprint each. buildings[].id identifies that volume. storeys[] inside a building is ordered bottom → top. For every solid shape, extrude the polygon as a vertical prism from elevation_m to elevation_m + height_m. That prism is the building body; do not add more masses.',
  '- shapes[].points = ordered [x, y] corners of that floor outline. Connect the last corner back to the first. Count the vertices — the rendered body must not have fewer or more corners than the ring.',
  '- Keep every jog and re-entrant corner. The axis-aligned bounding box is an envelope, not the plan. Do not replace a concave ring with that box.',
  '- cutout: true = a hole (courtyard/atrium) subtracted from that storey, not extra mass.',
  '- Several solid shapes on one storey = separate masses at that floor, not one merged block.',
  '- mezzanine: true = a partial floor inside the parent volume, not another full exterior storey.',
  '- elevation_m < 0 means that slab is below ground. If every elevation_m is >= 0, do not invent a basement, plinth, or extra floor under the building.',
  '- height_m is floor-to-floor. Align facade floor lines and window rows to these heights; do not assume a typical 3 m storey.',
  '- programs[] is occupancy for that storey. Adapt windows/shopfronts to the type (retail = shops, residential = apartments). Facade only — never extra mass.',
  '- Site the extrusion on flat, level ground. Roof is a flat slab unless the user description asks for a slope.',
  '- A map screenshot, when attached, is the silhouette to TRACE. The JSON confirms storey count per building and that no extra masses exist. Do not invent a different outline than the screenshot.',
].join('\n');

const GEOMETRY_JSON_READ_ME =
  'Extrude each storey.shapes polygon from elevation_m up by height_m (floor-to-floor). Match facade floor bands to height_m. programs[] = occupancy for shopfronts vs apartments. cutout=true is a hole. +x east, +y south, +z up.';

/**
 * Full (non-mezzanine) storey count. Mezzanines sit inside a parent volume
 * and must not be counted as extra exterior floors.
 */
export const countFullStoreys = (storeys: readonly Storey[]): number =>
  storeys.filter((storey) => !designGuards.is.mezzanineStorey(storey)).length;

/**
 * Exterior storey count for a render request. One building uses its full
 * storeys; several buildings use the tallest stack so two 3-storey volumes
 * are not reported as 6 floors.
 */
export const getRenderStoreyCount = (design: BuildingDesign): number =>
  countFullStoreys(getTallestBuildingStoreys(design));

/**
 * Mezzanine count of the tallest stack (same building as
 * {@link getRenderStoreyCount}), so two buildings with one mezzanine each are
 * not reported as two extra floors on one volume.
 */
export const getRenderMezzanineCount = (design: BuildingDesign): number =>
  countMezzanineStoreys(getTallestBuildingStoreys(design));

/**
 * Storeys of the tallest building. Ties break toward the stack with more
 * mezzanines so the massing header describes one real volume.
 */
const getTallestBuildingStoreys = (design: BuildingDesign): Storey[] => {
  if (design.buildings.length === 0) return [];
  return design.buildings.reduce((tallest, building) => {
    const full = countFullStoreys(building.storeys);
    const tallestFull = countFullStoreys(tallest);
    if (full > tallestFull) return building.storeys;
    if (full < tallestFull) return tallest;
    return countMezzanineStoreys(building.storeys) >
      countMezzanineStoreys(tallest)
      ? building.storeys
      : tallest;
  }, design.buildings[0]?.storeys ?? []);
};

/**
 * Mezzanine storeys in {@link storeys}. These sit inside a parent volume.
 */
export const countMezzanineStoreys = (storeys: readonly Storey[]): number =>
  storeys.filter((storey) => designGuards.is.mezzanineStorey(storey)).length;

/**
 * Storey lock for image models. A single building keeps one site-wide count;
 * several buildings are locked per footprint so they are not one taller stack.
 */
export const formatStoreyLockForDesign = (design: BuildingDesign): string => {
  if (design.buildings.length <= 1) {
    return formatSingleBuildingStoreyLock(getRenderStoreyCount(design));
  }

  const lines = design.buildings.map((building, index) => {
    const count = countFullStoreys(building.storeys);
    const noun = count === 1 ? 'storey' : 'storeys';
    const label = building.name
      ? `Building ${index + 1} (${building.name})`
      : `Building ${index + 1}`;
    return `${label}: exactly ${count} full exterior ${noun}.`;
  });

  return [
    'Storey lock (per building, not a single stacked count):',
    ...lines,
    'Do not merge these into one taller stack. Do not add or omit floors on any building.',
  ].join('\n');
};

/**
 * Single-building lock: one exterior count plus the usual not-N±1 wording.
 */
const formatSingleBuildingStoreyLock = (fullStoreyCount: number): string => {
  const noun = fullStoreyCount === 1 ? 'storey' : 'storeys';
  return `Storey lock: exactly ${fullStoreyCount} full exterior ${noun}. Match the stacked floor bands in the map screenshot when one is attached. Not ${fullStoreyCount + 1}. Not ${Math.max(0, fullStoreyCount - 1)}. Do not add a hidden extra floor, including under the building.`;
};

/**
 * Natural-language massing for image models: storey count, plan complexity,
 * dimensions, stacking, and setbacks. Coordinates stay in the JSON dump.
 */
export const describeBuildingDesignForRender = (
  design: BuildingDesign,
): string => {
  const fullStoreyCount = getRenderStoreyCount(design);
  const mezzanineCount = getRenderMezzanineCount(design);
  const totalGfa = Math.round(getDesignGfa(design));
  const buildingCount = design.buildings.length;
  const storeyNoun = fullStoreyCount === 1 ? 'storey' : 'storeys';
  const mezzanineBit =
    mezzanineCount > 0
      ? ` and ${mezzanineCount} mezzanine${mezzanineCount === 1 ? '' : 's'}`
      : '';
  const stackBit =
    buildingCount > 1
      ? `${buildingCount} buildings, tallest ${fullStoreyCount} full ${storeyNoun}${mezzanineBit}`
      : `${fullStoreyCount} full ${storeyNoun}${mezzanineBit}`;

  const header = [
    `Massing (authoritative): ${stackBit}.`,
    `Approx ${totalGfa} m² GFA.`,
    `Roof at ${formatMeters(roofHeightM(design))} m.`,
    buildingCount > 1
      ? 'Render each building as its own stack.'
      : 'Render exactly this stack.',
  ].join(' ');

  return [header, ...describeStoreyLinesForRender(design)].join('\n');
};

/**
 * Per-storey massing lines. Multiple buildings get a labelled header each.
 */
const describeStoreyLinesForRender = (design: BuildingDesign): string[] => {
  const buildings = design.buildings;
  if (buildings.length <= 1) {
    return getStoreys(design).map((storey, index) =>
      describeStoreyForRender(design, storey, index),
    );
  }

  return buildings.flatMap((building, buildingIndex) => {
    const label = building.name
      ? `Building ${buildingIndex + 1} (${building.name}):`
      : `Building ${buildingIndex + 1}:`;
    return [
      label,
      ...building.storeys.map((storey, index) =>
        describeStoreyForRender(design, storey, index),
      ),
    ];
  });
};

type GeometryShapeDump = {
  name?: string;
  cutout?: boolean;
  points: [number, number][];
};

type GeometryProgramDump = {
  type: ProgramActivityType;
  share?: number;
};

type GeometryStoreyDump = {
  name?: string;
  height_m: number;
  elevation_m: number;
  mezzanine?: boolean;
  programs?: GeometryProgramDump[];
  shapes: GeometryShapeDump[];
};

type GeometryBuildingDump = {
  id: string;
  name?: string;
  storeys: GeometryStoreyDump[];
};

type GeometryPayload = {
  read_me: string;
  units: string;
  axes: string;
  buildings: GeometryBuildingDump[];
};

/**
 * Legend + compact geometry JSON. Point rings stay on one line. Program
 * occupancy is included; calculated activities (stairwells, facades) are not.
 */
export const formatBuildingDesignGeometryJson = (
  design: BuildingDesign,
): string => {
  const payload: GeometryPayload = {
    read_me: GEOMETRY_JSON_READ_ME,
    units: 'meters',
    axes: '+x east, +y south, +z up',
    buildings: design.buildings.map((building) => {
      const dump: GeometryBuildingDump = {
        id: building.id,
        storeys: building.storeys.map((storey) => toStoreyDump(design, storey)),
      };
      if (building.name) dump.name = building.name;
      return dump;
    }),
  };

  return [
    GEOMETRY_JSON_LEGEND,
    '',
    '```json',
    stringifyGeometryPayload(payload),
    '```',
  ].join('\n');
};

/**
 * Single attachment: massing prose plus the JSON the model must extrude.
 */
export const buildBuildingDesignGeometryAttachment = (
  design: BuildingDesign,
): string => {
  return [
    describeBuildingDesignForRender(design),
    '',
    formatBuildingDesignGeometryJson(design),
  ].join('\n');
};

const describeStoreyForRender = (
  design: BuildingDesign,
  storey: Storey,
  index: number,
): string => {
  const elevation = getStoreyElevation(design, storey);
  const top = elevation + storey.height;
  const solids = getSolidShapes(storey);
  const cutouts = getCutoutShapes(storey);
  const footprint = Math.round(getStoreyGfaExcludingCutouts(storey));
  const label = storey.name ?? `Storey ${index + 1}`;
  const kind = storeyKind(storey, index);

  const parts = [
    `${label} (${kind}):`,
    describePlan(solids),
    `${footprint} m²,`,
    `floor-to-floor ${formatMeters(storey.height)} m`,
    `(slab ${formatMeters(elevation)} m to ${formatMeters(top)} m).`,
  ];

  if (cutouts.length > 0) {
    parts.push(
      `${cutouts.length} cutout void${cutouts.length === 1 ? '' : 's'} — keep empty.`,
    );
  }

  if (designGuards.is.mezzanineStorey(storey)) {
    parts.push('Sits inside its parent storey.');
  } else {
    const previous = previousFullStorey(design, storey);
    const change = describeFootprintChange(storey, previous);
    if (change) parts.push(change);
  }

  parts.push(formatPrograms(storey));

  return parts.join(' ');
};

const storeyKind = (storey: Storey, index: number): string => {
  if (designGuards.is.mezzanineStorey(storey)) {
    return 'mezzanine (not an extra full floor)';
  }
  if (index === 0) return 'ground';
  return 'full storey';
};

const describePlan = (solids: Shape[]): string => {
  if (solids.length === 0) return 'no plan shapes,';
  if (solids.length === 1) {
    const shape = solids[0];
    if (!shape) return 'no plan shapes,';
    return `${describeShapePlan(shape)},`;
  }
  const masses = solids
    .map((shape, index) => `mass ${index + 1} ${describeShapePlan(shape)}`)
    .join('; ');
  return `${solids.length} separate masses (${masses}),`;
};

const describeShapePlan = (shape: Shape): string => {
  const corners = uniqueCornerCount(shape.points);
  const bounds = getPlanBounds(shape.points);
  if (corners === 4) {
    if (!bounds) return 'rectangular 4-corner plan';
    return `rectangular 4-corner plan ${formatMeters(bounds.width)} × ${formatMeters(bounds.depth)} m`;
  }
  const form = `${corners}-corner plan (not a rectangle; do not fill the envelope)`;
  if (!bounds) return form;
  return `${form} ${formatMeters(bounds.width)} × ${formatMeters(bounds.depth)} m envelope — follow the polygon jogs`;
};

const describeFootprintChange = (
  storey: Storey,
  previous: Storey | undefined,
): string | undefined => {
  if (!previous) return undefined;
  const currentArea = getStoreyGfaExcludingCutouts(storey);
  const previousArea = getStoreyGfaExcludingCutouts(previous);
  if (previousArea <= 0) return undefined;
  const ratio = currentArea / previousArea;
  if (ratio <= SETBACK_RATIO) {
    return 'Set back vs the storey below — do not extrude as one box.';
  }
  if (ratio >= OVERHANG_RATIO) {
    return 'Overhangs the storey below — keep that cantilever.';
  }
  return 'Same overall footprint as the storey below.';
};

/**
 * Previous non-mezzanine storey in the same building as {@link storey}.
 */
const previousFullStorey = (
  design: BuildingDesign,
  storey: Storey,
): Storey | undefined => {
  const siblings =
    design.buildings.find((building) =>
      building.storeys.some((item) => item.id === storey.id),
    )?.storeys ?? getStoreys(design);
  const index = siblings.findIndex((item) => item.id === storey.id);
  for (let i = index - 1; i >= 0; i--) {
    const candidate = siblings[i];
    if (candidate && !designGuards.is.mezzanineStorey(candidate)) {
      return candidate;
    }
  }
  return undefined;
};

const formatPrograms = (storey: Storey): string => {
  const programs = storey.activities.filter(
    activityRefGuards.is.programActivityRef,
  );
  if (programs.length === 0) {
    return 'Program: unprogrammed shell — simple facade, do not invent a building type.';
  }
  const parts = programs.map((ref) => {
    const label = PROGRAM_FACADE_LABEL[ref.type];
    const hint = PROGRAM_FACADE_HINT[ref.type];
    if (guards.is.number(ref.share)) {
      return `${label} ${Math.round(ref.share * 100)}% (${hint})`;
    }
    return `${label} (${hint})`;
  });
  return `Program: ${parts.join('; ')}. Facade character only — do not add volumes.`;
};

const roofHeightM = (design: BuildingDesign): number => {
  let maxTop = 0;
  for (const storey of getAllStoreys(design)) {
    if (designGuards.is.mezzanineStorey(storey)) continue;
    const top = getStoreyElevation(design, storey) + storey.height;
    if (top > maxTop) maxTop = top;
  }
  return maxTop;
};

const toProgramDumps = (storey: Storey): GeometryProgramDump[] => {
  return storey.activities
    .filter(activityRefGuards.is.programActivityRef)
    .map((ref) => {
      const dump: GeometryProgramDump = { type: ref.type };
      if (guards.is.number(ref.share)) dump.share = roundToDecimals(ref.share);
      return dump;
    });
};

const uniqueCornerCount = (points: Point2D[]): number => {
  if (points.length <= 1) return points.length;
  const first = points[0];
  const last = points[points.length - 1];
  if (first && last && isEqualPoints(first, last)) return points.length - 1;
  return points.length;
};

const getPlanBounds = (
  points: Point2D[],
): { width: number; depth: number } | undefined => {
  if (points.length === 0) return undefined;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    const x = point[0];
    const y = point[1];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { width: maxX - minX, depth: maxY - minY };
};

const toStoreyDump = (
  design: BuildingDesign,
  storey: Storey,
): GeometryStoreyDump => {
  const dump: GeometryStoreyDump = {
    height_m: roundToDecimals(storey.height),
    elevation_m: roundToDecimals(getStoreyElevation(design, storey)),
    shapes: storey.shapes.map(toShapeDump),
  };
  if (storey.name) dump.name = storey.name;
  if (storey.mezzanine === true) dump.mezzanine = true;
  const programs = toProgramDumps(storey);
  if (programs.length > 0) dump.programs = programs;
  return dump;
};

const toShapeDump = (shape: Shape): GeometryShapeDump => {
  const dump: GeometryShapeDump = { points: shape.points.map(roundPoint) };
  if (shape.name) dump.name = shape.name;
  if (shape.cutout === true) dump.cutout = true;
  return dump;
};

/**
 * Pretty-print buildings but keep each point ring on one line so the plan is visible.
 */
const stringifyGeometryPayload = (payload: GeometryPayload): string => {
  const buildingBlocks = payload.buildings.map((building) => {
    const storeyBlocks = building.storeys.map(stringifyStoreyDump);
    const lines = [
      `      "id": ${JSON.stringify(building.id)},`,
      building.name != null
        ? `      "name": ${JSON.stringify(building.name)},`
        : undefined,
      '      "storeys": [',
      storeyBlocks.join(',\n'),
      '      ]',
    ].filter(guards.is.defined);
    return `    {\n${lines.join('\n')}\n    }`;
  });

  return [
    '{',
    `  "read_me": ${JSON.stringify(payload.read_me)},`,
    `  "units": ${JSON.stringify(payload.units)},`,
    `  "axes": ${JSON.stringify(payload.axes)},`,
    '  "buildings": [',
    buildingBlocks.join(',\n'),
    '  ]',
    '}',
  ].join('\n');
};

/**
 * One storey object inside a building dump.
 */
const stringifyStoreyDump = (storey: GeometryStoreyDump): string => {
  const lines = [
    storey.name != null
      ? `        "name": ${JSON.stringify(storey.name)},`
      : undefined,
    `        "height_m": ${storey.height_m},`,
    `        "elevation_m": ${storey.elevation_m},`,
    storey.mezzanine === true ? '        "mezzanine": true,' : undefined,
    programsJsonLine(storey.programs),
    '        "shapes": [',
    storey.shapes.map(stringifyShapeDump).join(',\n'),
    '        ]',
  ].filter(guards.is.defined);
  return `      {\n${lines.join('\n')}\n      }`;
};

const programsJsonLine = (
  programs: GeometryProgramDump[] | undefined,
): string | undefined => {
  if (!programs?.length) return undefined;
  return `        "programs": ${stringifyProgramDumps(programs)},`;
};

const stringifyProgramDumps = (programs: GeometryProgramDump[]): string => {
  const items = programs.map((program) => {
    const fields = [
      `"type": ${JSON.stringify(program.type)}`,
      guards.is.number(program.share) ? `"share": ${program.share}` : undefined,
    ].filter(guards.is.defined);
    return `{ ${fields.join(', ')} }`;
  });
  return `[${items.join(', ')}]`;
};

const stringifyShapeDump = (shape: GeometryShapeDump): string => {
  const points = `[${shape.points.map((point) => `[${point[0]}, ${point[1]}]`).join(', ')}]`;
  const fields = [
    shape.name != null ? `"name": ${JSON.stringify(shape.name)}` : undefined,
    shape.cutout === true ? '"cutout": true' : undefined,
    `"points": ${points}`,
  ].filter(guards.is.defined);
  return `          { ${fields.join(', ')} }`;
};

const roundPoint = (point: Point2D): [number, number] => [
  roundToDecimals(point[0]),
  roundToDecimals(point[1]),
];

const formatMeters = (value: number): string =>
  roundToDecimals(value, 2).toFixed(2);
